import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { CaseTableHead, CaseTableRow } from '@/pages/ibama/CasesPage';
import { fetchIbamaEntity } from '@/services/ibama';

/**
 * One company, by CNPJ: the autos issued against it (master), the ledger
 * cases among them (with instance dates and collection state), its seizure
 * and suspension terms (an enforcement act, never a fine), the notifications
 * served on it (the step before an auto, counted) and its CTF registrations.
 *
 * Every `Answer` is record-scoped: a refusal means THIS company lacks the
 * substrate (no auto, no ledger case), never that IBAMA cannot support the
 * analysis. Natural persons have no page here at all.
 */
export function IbamaEntityProfilePage() {
  const { cnpj = '' } = useParams();
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'entity', cnpj],
    queryFn: () => fetchIbamaEntity(cnpj),
    enabled: cnpj.length > 0,
    // A 404 is an answer (no such CNPJ in any IBAMA table), not a transient failure.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-6">
        <ErrorMessage
          message={notFound ? t('ibama:entity.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const span = (first: number | null, last: number | null) =>
    first && last ? `${first}–${last}` : '—';

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/ibama/entities" className="text-primary underline-offset-2 hover:underline">
            {t('ibama:entity.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold text-foreground">{data.entity.name}</h1>
        <p className="text-sm tabular-nums text-muted-foreground">
          {t('ibama:entity.cnpj', { cnpj: data.entity.cnpj })}
          {data.entity.cnpj_valid === false ? ` · ${t('ibama:entity.cnpjInvalid')}` : ''}
          {data.entity.registry_source
            ? ` · ${t('ibama:entity.firstSeen', {
                source: t(`ibama:entity.sources.${data.entity.registry_source}`, {
                  defaultValue: data.entity.registry_source,
                }),
              })}`
            : ''}
        </p>
      </header>

      <ExplainerCard i18nKey="ibama:entity.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-autos>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ibama:entity.autos.title')}
          </h2>
          <Answer answer={data.autos} scope="record" help={t('ibama:entity.autos.help')}>
            {(a) => (
              <div className="space-y-2">
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  {(
                    [
                      ['autos', number.format(a.autos)],
                      ['withLedgerCase', number.format(a.with_ledger_case)],
                      ['cancelled', number.format(a.cancelled)],
                      ['years', span(a.first_year, a.last_year)],
                      [
                        'faceValueTotal',
                        a.face_value_total === null ? '—' : currency.format(a.face_value_total),
                      ],
                    ] as const
                  ).map(([key, value]) => (
                    <div
                      key={key}
                      className="rounded-md border bg-muted/30 p-2"
                      data-autos-field={key}
                    >
                      <dt className="text-xs text-muted-foreground">
                        {t(`ibama:entity.autos.${key}`)}
                      </dt>
                      <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-muted-foreground" data-autos-natures>
                  {Object.entries(a.by_nature)
                    .map(
                      ([nature, n]) =>
                        `${
                          nature === 'UNCLASSIFIED' ? t('ibama:entity.autos.unclassified') : nature
                        }: ${number.format(n)}`,
                    )
                    .join(' · ')}
                </p>
                <p className="text-xs text-muted-foreground" data-autos-types>
                  {Object.entries(a.by_auto_type)
                    .map(([type, n]) => `${type}: ${number.format(n)}`)
                    .join(' · ')}
                </p>
              </div>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-ledger>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ibama:entity.ledger.title')}
          </h2>
          <Answer answer={data.ledger} scope="record" help={t('ibama:entity.ledger.help')}>
            {(l) => (
              <div className="space-y-2">
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  {(
                    [
                      ['cases', number.format(l.cases)],
                      [
                        'settled',
                        `${number.format(l.settled)} (${percent.format(l.settled_share)})`,
                      ],
                      ['firstDecided', number.format(l.first_decided)],
                      ['boardDated', number.format(l.board_dated)],
                      ['years', span(l.first_year, l.last_year)],
                      [
                        'brlFaceValueTotal',
                        `${currency.format(l.brl_face_value_total)} (${number.format(l.brl_cases)})`,
                      ],
                    ] as const
                  ).map(([key, value]) => (
                    <div
                      key={key}
                      className="rounded-md border bg-muted/30 p-2"
                      data-ledger-field={key}
                    >
                      <dt className="text-xs text-muted-foreground">
                        {t(`ibama:entity.ledger.${key}`)}
                      </dt>
                      <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-muted-foreground" data-ledger-outcomes>
                  {Object.entries(l.by_outcome)
                    .map(
                      ([code, n]) =>
                        `${t(`ibama:overview.outcomes.${code}`, { defaultValue: code })}: ${number.format(n)}`,
                    )
                    .join(' · ')}
                </p>
              </div>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="ibama:entity.guide.ledger" />
          </div>
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-instruments>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ibama:entity.instruments.title')}
          </h2>
          <Answer
            answer={data.instruments}
            scope="record"
            help={t('ibama:entity.instruments.help')}
          >
            {(i) => (
              <dl className="grid grid-cols-3 gap-2 text-sm">
                {(
                  [
                    ['seizures', number.format(i.seizure_terms)],
                    ['suspensions', number.format(i.suspension_terms)],
                    ['years', span(i.first_year, i.last_year)],
                  ] as const
                ).map(([key, value]) => (
                  <div
                    key={key}
                    className="rounded-md border bg-muted/30 p-2"
                    data-instrument-field={key}
                  >
                    <dt className="text-xs text-muted-foreground">
                      {t(`ibama:entity.instruments.${key}`)}
                    </dt>
                    <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-notifications>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ibama:entity.notifications.title')}
          </h2>
          <Answer
            answer={data.notifications}
            scope="record"
            help={t('ibama:entity.notifications.help')}
          >
            {(n) => (
              <dl className="grid grid-cols-3 gap-2 text-sm">
                {(
                  [
                    ['notifications', number.format(n.notifications)],
                    ['autoIssued', number.format(n.auto_issued)],
                    ['years', span(n.first_year, n.last_year)],
                  ] as const
                ).map(([key, value]) => (
                  <div
                    key={key}
                    className="rounded-md border bg-muted/30 p-2"
                    data-notification-field={key}
                  >
                    <dt className="text-xs text-muted-foreground">
                      {t(`ibama:entity.notifications.${key}`)}
                    </dt>
                    <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Answer>
        </section>
      </div>

      <section className="rounded-lg border bg-card p-4" data-registrations>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('ibama:entity.registrations.title', {
            n: number.format(data.registrations_total),
          })}
        </h2>
        <p className="mb-2 text-xs text-muted-foreground">{t('ibama:entity.registrations.help')}</p>
        {data.registrations.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('ibama:entity.registrations.none')}</p>
        ) : (
          <div className="max-h-[40vh] overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-muted text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 font-medium">
                    {t('ibama:entity.registrations.registry')}
                  </th>
                  <th className="px-2 py-1 font-medium">
                    {t('ibama:entity.registrations.activity')}
                  </th>
                  <th className="px-2 py-1 font-medium">{t('ibama:entity.registrations.place')}</th>
                  <th className="px-2 py-1 font-medium">
                    {t('ibama:entity.registrations.period')}
                  </th>
                  <th className="px-2 py-1 font-medium">
                    {t('ibama:entity.registrations.status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.registrations.map((reg, index) => (
                  <tr key={index} className="border-t" data-registration>
                    <td className="px-2 py-1 text-foreground">
                      {t(`ibama:entities.registries.${reg.registry}`, {
                        defaultValue: reg.registry,
                      })}
                    </td>
                    <td className="px-2 py-1 text-muted-foreground">
                      {reg.activity_description ?? reg.category_description ?? '—'}
                    </td>
                    <td className="px-2 py-1 text-muted-foreground">
                      {[reg.municipality, reg.uf].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-2 py-1 tabular-nums text-muted-foreground">
                      {reg.activity_start ?? '—'}
                      {reg.activity_end ? ` → ${reg.activity_end}` : ''}
                    </td>
                    <td className="px-2 py-1 text-muted-foreground">
                      {reg.registration_status ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.registrations_total > data.registrations.length ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('ibama:entity.registrations.capped', {
              shown: number.format(data.registrations.length),
              total: number.format(data.registrations_total),
            })}
          </p>
        ) : null}
      </section>

      <section className="rounded-lg border bg-card" data-cases>
        <div className="flex items-center justify-between px-4 py-2">
          <h2 className="text-sm font-semibold text-foreground">
            {t('ibama:entity.cases.title', { n: number.format(data.cases.length) })}
          </h2>
          <Link
            to={`/ibama/cases?entity=${data.entity.cnpj}`}
            className="text-xs text-primary underline-offset-2 hover:underline"
          >
            {t('ibama:entity.cases.openList')}
          </Link>
        </div>
        {data.cases.length === 0 ? (
          <p className="px-4 pb-3 text-xs text-muted-foreground">{t('ibama:entity.cases.none')}</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <CaseTableHead />
              </thead>
              <tbody>
                {data.cases.map((row) => (
                  <CaseTableRow key={row.key} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
